// M4 world §12 — politics, sanctions and region risk 2000–2029.
//   sanctions       m4/regions.js SANCTIONS via sanctionsAt(region, y, { usCompany }); 'western' rows bind every player
//                   company. exportBan → sell to dark-fleet buyers at (1 − buyerDiscount); newInvestmentBan → no new
//                   leases / spuds / storage (m2 regionBlock hunk); techBan → western services refuse Arctic-offshore,
//                   deepwater or shale work (drill gate idles the rig); divestBy → sell out (action) before the date or
//                   the assets are frozen (no divestment, production continues under the ban); priceCap → western
//                   shipping only at ≤ cap (Kazakh CPC exempt).
//   nationalizations m4/regions.js M4_NATIONALIZATIONS: Venezuela 2007 (accept 40% or refuse → seized at 20%),
//                   Kashagan 2008 (+8.3% to KMG), YPF 2012 (state partner: flavour + capital controls), Russia 2022
//                   (western stakes seized at 5%).
//   capital controls Argentina: export duty and 2007 price cap on the price; (1 − repatriationShare) of revenue trapped
//                   in pesos losing pesoLossPerYear; released when repatriation is free again.
//   events          Uri freeze (Feb 2021: wells in the listed regions shut 7 d unless winterized), Fort McMurray
//                   wildfire (May 2016: listed oil-sands fields shut 30 d), named storms 2004–2024 (M3 storm model),
//                   M4 lease rounds (M3 round machinery).
import { allWells } from '../indexes.js';
import { yearFrac, dateToDay } from '../../core/time.js';
import { fieldDef, regionDef, yf, regionRisk } from '../defs.js';
import { SANCTIONS, sanctionsAt, M4_NATIONALIZATIONS, capitalControlsAt } from '../../data/m4/regions.js';
import { HISTORICAL_STORMS_M4, LEASE_ROUNDS_M4 } from '../../data/m4/rules.js';
import { spotPrice } from '../../economy/market.js';
import { notify, fmt$ } from '../util.js';
import { hasAssets, seizeRegion, bookValue } from '../m2/politics.js';
import { queueDecision, popDecision } from '../security.js';
import { newStormDev, warningDaysFor } from '../m3/storms.js';
import { openRound } from '../m3/leaserounds.js';
import { hasUpgrade } from '../research.js';
import { W, peekW, m4On, isUsCo, isWestern, regionOf, ownWell, GONE } from './worldCore.js';

export const DIVEST_FRACTION = 0.3;          // fire-sale value of a sanctioned position (share of book)
const D_UKRAINE = dateToDay(2022, 2, 24);

// ---- sanctions ------------------------------------------------------------------------------------------------------
export function sanctionsFor(state, regionId, y = yf(state)) {
  if (!regionId || y < 2000) return [];
  return sanctionsAt(regionId, y, { usCompany: isUsCo(state) }).filter((s) => s.appliesTo !== 'western' || isWestern(state));
}
const SANCTION_REGIONS = new Set(SANCTIONS.flatMap((s) => s.regionIds));
export const isSanctionRegion = (rid) => SANCTION_REGIONS.has(rid);

// Western services refuse this well's work? (reason or null)
export function techBanReason(state, fieldId, waterFt = 0) {
  const rid = regionOf(fieldId);
  if (!SANCTION_REGIONS.has(rid)) return null;
  const fdef = fieldDef(fieldId);
  for (const s of sanctionsFor(state, rid)) {
    const tb = s.effects?.techBan;
    if (!tb?.length) continue;
    const arctic = (fdef?.environment?.kind === 'arctic' || fdef?.environment?.ice?.kind === 'sea_ice') && waterFt > 0;
    if ((tb.includes('arctic_offshore') && arctic) || (tb.includes('deepwater') && waterFt >= 500) || (tb.includes('shale') && fdef?.shale)) {
      return `sanctions (${s.id.replace(/_/g, ' ')}): western contractors and gear may not work on ${fdef?.name ?? fieldId}. The rig is idled.`;
    }
  }
  return null;
}

// m2 regionBlock hunk: new investment banned (no leases, spuds, storage).
export function m4RegionBlock(state, fieldId) {
  if (!m4On(state)) return null;
  const rid = regionOf(fieldId);
  if (!SANCTION_REGIONS.has(rid)) return null;
  const s = sanctionsFor(state, rid).find((x) => x.effects?.newInvestmentBan);
  return s ? `${regionDef(rid)?.name ?? rid}: sanctions bar new investment (${s.note ?? s.id}).` : null;
}

// Price multiplier from sanctions on a crude worth `price` $/bbl at the wellhead: dark-fleet discount, price cap.
export function sanctionPriceMult(state, fieldId, price) {
  const rid = regionOf(fieldId);
  if (!SANCTION_REGIONS.has(rid)) return 1;
  const y = yf(state);
  let m = 1;
  for (const s of sanctionsFor(state, rid, y)) {
    const e = s.effects ?? {};
    if (e.buyerDiscount) m *= 1 - e.buyerDiscount;
    if (e.priceCap && price > 0) {
      const cap = e.priceCapFrom2025_7 && y >= 2025.7 ? e.priceCapFrom2025_7 : e.priceCap;
      if (price * m > cap) m *= cap / (price * m);
    }
  }
  return m;
}

// Capital controls on the price (export duty, administrative price cap).
export function capitalControlPriceMult(state, fieldId, price) {
  const cc = capitalControlsAt(regionOf(fieldId), yf(state));
  if (!cc) return 1;
  let m = 1 - (cc.exportDuty ?? 0);
  if (cc.priceCap && price * m > cc.priceCap) m *= cc.priceCap / (price * m);
  return m;
}

// production.shipAndSell hook (after the sale): trap the non-repatriable share of revenue in pesos.
export function capitalControlsOnSale(state, fieldId, gross) {
  const rid = regionOf(fieldId);
  const cc = capitalControlsAt(rid, yf(state));
  if (!cc || !(gross > 0) || (cc.repatriationShare ?? 1) >= 1) return 0;
  const trapped = gross * (1 - cc.repatriationShare);
  state.company.cash -= trapped;
  const Wm = W(state);
  Wm.pesos[rid] = (Wm.pesos[rid] ?? 0) + trapped;
  return trapped;
}

// Monthly: trapped pesos lose value; freed when repatriation is open again.
export function tickPesos(state) {
  const Wm = peekW(state);
  if (!Wm) return;
  for (const [rid, bal] of Object.entries(Wm.pesos)) {
    if (!(bal > 0)) continue;
    const cc = capitalControlsAt(rid, yf(state));
    if (!cc || (cc.repatriationShare ?? 1) >= 1) {
      state.company.cash += bal;
      Wm.pesos[rid] = 0;
      notify(state, 'news', { kind: 'market', regionId: rid, level: 'info', text: `${regionDef(rid)?.name ?? rid} lifts its capital controls: ${fmt$(bal)} of trapped pesos repatriated.` });
      continue;
    }
    const loss = bal * (cc.pesoLossPerYear ?? 0) / 12;
    Wm.pesos[rid] = bal - loss;
    state.company.stats.pesoLosses = (state.company.stats.pesoLosses ?? 0) + loss;
  }
}

// Divest a sanctioned position before divestBy (fire sale at DIVEST_FRACTION of book).
export function divestRegionImpl(state, regionId) {
  if (!m4On(state)) return { ok: false, msg: 'Nothing to divest.' };
  const s = sanctionsFor(state, regionId).find((x) => x.effects?.divestBy);
  if (!s) return { ok: false, msg: `No sanctions order requires you to leave ${regionDef(regionId)?.name ?? regionId}.` };
  if (yf(state) >= s.effects.divestBy) return { ok: false, msg: 'Too late: the assets are frozen. No buyer may take them off your hands now.' };
  if (!hasAssets(state, regionId)) return { ok: false, msg: 'You hold nothing there.' };
  const value = bookValue(state, regionId);
  const r = seizeRegion(state, regionId, DIVEST_FRACTION, `Sold under sanctions (${s.id.replace(/_/g, ' ')})`, { compensationDays: 30 });
  W(state).nat[`divest:${regionId}`] = { day: state.day, value: Math.round(value), proceeds: Math.round(value * DIVEST_FRACTION) };
  return r ? { ok: true, msg: `Sold out of ${regionDef(regionId)?.name ?? regionId}: ${fmt$(value * DIVEST_FRACTION)} for assets on the books at ${fmt$(value)}.` } : { ok: false, msg: 'Nothing was sold.' };
}

// Daily: sanction start notices (bus event 'sanction') where the player holds assets.
export function tickSanctionNotices(state) {
  const Wm = W(state);
  const y = yf(state);
  for (const s of SANCTIONS) {
    if (y < s.from || y >= s.until || Wm.regimesSeen[`sanction:${s.id}`] != null) continue;
    Wm.regimesSeen[`sanction:${s.id}`] = state.day;
    const binds = s.appliesTo === 'all' || (s.appliesTo === 'us' ? isUsCo(state) : isWestern(state));
    notify(state, 'sanction', { id: s.id, regionIds: [...s.regionIds], binds, text: s.note ?? s.id });
    const present = s.regionIds.filter((rid) => regionDef(rid) && state.regions?.[rid] && hasAssets(state, rid));
    if (binds && present.length) {
      const e = s.effects ?? {};
      const parts = [e.exportBan ? `crude only to sanctioned buyers at ${Math.round((e.buyerDiscount ?? 0) * 100)}% off` : null, e.newInvestmentBan ? 'no new investment' : null,
        e.techBan ? `no western ${e.techBan.join(' / ').replace(/_/g, ' ')} services` : null, e.priceCap ? `western shipping only at ≤ $${e.priceCap}` : null,
        e.divestBy ? `sell out by ${Math.floor(e.divestBy)} or be frozen` : null].filter(Boolean);
      notify(state, 'news', { kind: 'politics', regionId: present[0], pause: true, level: 'major', text: `Sanctions on ${present.map((r) => regionDef(r)?.name ?? r).join(', ')}: ${parts.join('; ') || 'restrictions apply'}.` });
    }
  }
}

// ---- nationalizations ---------------------------------------------------------------------------------------------
const natById = (id) => M4_NATIONALIZATIONS.find((n) => n.id === id) ?? null;
export const M4_NAT_REGIONS = new Set(M4_NATIONALIZATIONS.filter((n) => n.mode === 'participation' && n.share > 0).map((n) => n.regionId));

// m3 nationalize.wellNat hook: the M4 participation share for this well, or null.
export function m4WellNat(state, w) {
  const Wm = peekW(state);
  if (!Wm?.nat) return null;
  const rid = regionOf(w.fieldId);
  if (!M4_NAT_REGIONS.has(rid)) return null;
  let best = null;
  for (const n of M4_NATIONALIZATIONS) {
    if (n.regionId !== rid || n.mode !== 'participation' || !(n.share > 0)) continue;
    const rec = Wm.nat[n.id];
    if (!rec || rec.choice === 'refuse' || rec.choice === 'pending') continue;
    if (n.fieldIds && !n.fieldIds.includes(w.fieldId)) continue;
    if (!best || n.share > best.share) best = { mode: 'participation', share: n.share, rec: { id: n.id, buybackDiscount: 0, reason: n.reason } };
  }
  return best;
}

export function applyM4Nationalization(state, id) {
  const n = natById(id);
  if (!n || !m4On(state)) return null;
  const Wm = W(state);
  if (Wm.nat[n.id]) return Wm.nat[n.id];
  if (yf(state) < n.from - 1 / 365 && !(n.id === 'russia_2022' && state.day >= D_UKRAINE)) return null;
  const rid = n.regionId;
  const name = regionDef(rid)?.name ?? rid;
  const present = !!regionDef(rid) && !!state.regions?.[rid] && hasAssets(state, rid);
  const rec = (Wm.nat[n.id] = { id: n.id, day: state.day, choice: null, present });
  if (n.appliesTo === 'western' && !isWestern(state)) { rec.choice = 'exempt'; return rec; }
  if (n.appliesTo === 'statePartner') {
    rec.choice = 'partner';
    if (present) notify(state, 'nationalization', { regionId: rid, mode: 'statePartner', share: 0, pause: false, level: 'warn', text: `${n.reason} Your concessions stand, but expect capital controls and a state partner at the table.` });
    return rec;
  }
  if (n.mode === 'seize') {
    if (yf(state) < n.from) { rec.choice = 'announced'; rec.seizeFrom = n.from; if (present) notify(state, 'nationalization', { regionId: rid, mode: 'warning', pause: true, level: 'major', text: `${name}: the western majors are leaving. Sell out while you can — stakes still held in ${Math.floor(n.from)} will be seized for ${Math.round((n.compensation ?? 0) * 100)}% of book.` }); return rec; }
    rec.choice = 'seized';
    if (present) seizeRegion(state, rid, n.compensation ?? 0, n.reason, { compensationDays: Math.min(n.compensationDays ?? 365, 3650) });
    return rec;
  }
  if (!present) { rec.choice = n.appliesTo === 'refusers' ? 'accept' : 'applied'; return rec; }
  if (n.appliesTo === 'refusers') {
    rec.choice = 'pending';
    rec.deadlineDay = state.day + (n.decisionDays ?? 60);
    queueDecision(state, {
      kind: 'm4Nationalization', id: `m4nat_${n.id}`, natId: n.id, regionId: rid, day: state.day, deadlineDay: rec.deadlineDay,
      title: `${name}: sign the new terms?`,
      text: `${n.reason} Sign and the state company takes ${Math.round(n.share * 100)}% of every barrel (and of the costs); refuse and your assets are seized for ${Math.round((n.refuserCompensation ?? 0) * 100)}% of book, payable — eventually — after arbitration.`,
      options: [{ choice: 'accept', label: `Sign: keep ${Math.round((1 - n.share) * 100)}% as a minority partner` }, { choice: 'refuse', label: 'Refuse and go to arbitration' }],
    });
    notify(state, 'nationalization', { regionId: rid, mode: 'participation', share: n.share, pause: true, level: 'major', text: `${name}: ${Math.round((n.decisionDays ?? 60))} days to accept minority terms or be expropriated.` });
    return rec;
  }
  // 'all' participation (Kashagan 2008): applied at once, compensated
  rec.choice = 'applied';
  const fieldBook = bookValue(state, rid);
  const comp = fieldBook * n.share * (n.compensation ?? 0);
  if (comp > 0.5) state.politics?.compensations?.push({ regionId: rid, amount: Math.round(comp * 100) / 100, dueDay: state.day + (n.compensationDays ?? 365), reason: n.reason });
  notify(state, 'nationalization', { regionId: rid, mode: 'participation', share: n.share, compensation: comp, pause: true, level: 'major', text: `${n.reason} The state partner now takes ${(n.share * 100).toFixed(1)}% of your barrels${n.fieldIds ? ` at ${n.fieldIds.map((f) => fieldDef(f)?.name ?? f).join(', ')}` : ''}.` });
  return rec;
}

export function resolveM4NatImpl(state, d, choice) {
  const n = natById(d.natId);
  const rec = peekW(state)?.nat?.[d.natId];
  popDecision(state, d.id);
  if (!n || !rec) return { ok: true, msg: 'The offer lapsed.' };
  if (choice === 'refuse') {
    rec.choice = 'refuse';
    const r = seizeRegion(state, n.regionId, n.refuserCompensation ?? 0, `${n.reason} (you refused the new terms)`, { compensationDays: Math.min(n.compensationDays ?? 365, 3650) });
    return { ok: true, msg: r ? `Refused. Your ${regionDef(n.regionId)?.name} assets were seized; arbitration may pay ${fmt$(r.compensation)} in years.` : 'Refused.' };
  }
  rec.choice = 'accept';
  return { ok: true, msg: `Signed: PDVSA-style mixed company. The state takes ${Math.round(n.share * 100)}% of production and costs.` };
}

export function tickNationalizationsM4(state) {
  const y = yf(state);
  const Wm = W(state);
  for (const n of M4_NATIONALIZATIONS) {
    const rec = Wm.nat[n.id];
    if (!rec && y >= n.from && y < n.until) applyM4Nationalization(state, n.id);
    else if (rec?.choice === 'announced' && y >= n.from) {
      rec.choice = 'seized';
      if (hasAssets(state, n.regionId)) seizeRegion(state, n.regionId, n.compensation ?? 0, n.reason, { compensationDays: Math.min(n.compensationDays ?? 365, 3650) });
    } else if (rec?.choice === 'pending' && state.day >= rec.deadlineDay) {
      resolveM4NatImpl(state, { id: `m4nat_${n.id}`, natId: n.id }, 'accept'); // silence = signing (most partners signed)
    }
  }
}

// ---- occupations (Libya 2011, 2013, 2020 …): wells in an occupied region stay shut (≤ 400 d, the M3 cap). The M3
// path sets forcedUntil = day + 1 after production has run, which expires before the next day's production.
export const OCCUPATION_MAX_DAYS = 400;
export function tickOccupationsM4(state) {
  for (const [rid, reg] of Object.entries(state.regions ?? {})) {
    if (!(reg.occupiedUntil > state.day)) continue;
    const start = reg.m4OccStart ??= state.day;
    if (state.day >= Math.min(reg.occupiedUntil, start + OCCUPATION_MAX_DAYS)) continue;
    for (const w of allWells(state)) if (w.prod && w.status === 'PRODUCING' && ownWell(w) && regionOf(w.fieldId) === rid) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + 2);
  }
  for (const reg of Object.values(state.regions ?? {})) if (reg.m4OccStart != null && !(reg.occupiedUntil > state.day)) reg.m4OccStart = null;
}

// ---- Uri freeze, Fort McMurray wildfire -----------------------------------------------------------------------------
const winterized = (state, w) => !!w.winterized || (fieldDef(w.fieldId)?.environment?.coldC ?? 0) <= -20 || hasUpgrade(state, 'winterization');
export function applyFreeze(state, e) {
  const Wm = W(state);
  const days = e.days ?? 7;
  const rids = new Set(e.regionIds ?? []);
  let n = 0;
  for (const w of allWells(state)) {
    if (!ownWell(w) || w.status !== 'PRODUCING' || !w.prod || !rids.has(regionOf(w.fieldId)) || winterized(state, w)) continue;
    w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + days);
    w.frozen = { day: state.day, untilDay: state.day + days };
    n++;
  }
  Wm.events.freeze = { day: state.day, untilDay: state.day + days, regionIds: [...rids], wells: n, gasPriceMult: 6 };
  if (n) notify(state, 'news', { kind: 'disaster', regionId: e.regionIds?.[0], pause: true, level: 'major', text: `Deep freeze: ${n} of your wells froze off and are shut in for ${days} days. Gas prices spike sixfold.` });
  return n;
}
// B (gas): Henry Hub already carries the Uri shock (rules.HENRY_HUB_SHOCKS); this reports the freeze window.
export function freezeActive(state, regionId) {
  const f = peekW(state)?.events?.freeze;
  return !!f && state.day < f.untilDay && (!regionId || f.regionIds.includes(regionId));
}

export function applyWildfire(state, e) {
  const Wm = W(state);
  const days = e.days ?? 30;
  const until = state.day + days;
  let n = 0;
  for (const fid of e.fieldIds ?? []) {
    if (!state.fields[fid]) continue;
    for (const w of allWells(state)) if (w.fieldId === fid && ownWell(w) && w.prod) { w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until); n++; }
    const os = Wm.oilSands[fid];
    if (os) os.shutUntil = Math.max(os.shutUntil ?? 0, until);
    (Wm.events.wildfire ??= {})[fid] = until;
  }
  const mines = (e.fieldIds ?? []).filter((f) => Wm.oilSands[f]?.blocks?.length || Wm.oilSands[f]?.plants);
  if (n || mines.length) notify(state, 'news', { kind: 'disaster', fieldId: e.fieldIds?.[0], pause: true, level: 'major', text: `Wildfire: Fort McMurray is evacuated. Your oil-sands operations are shut in for ${days} days — crews and families first.` });
  return n;
}
export function wildfireShut(state, fieldId) { const u = peekW(state)?.events?.wildfire?.[fieldId]; return u != null && state.day < u; }

// ---- named storms 2004–2024 (M3 storm model) --------------------------------------------------------------------------
export function tickStormsM4(state) {
  const warn = warningDaysFor(state, 'hurricane');
  const Wm = W(state);
  for (const hs of HISTORICAL_STORMS_M4) {
    if (hs.day - warn === state.day && !Wm.events[`storm:${hs.id}`]) {
      const fids = hs.fieldIds.filter((f) => state.fields[f] && fieldDef(f));
      Wm.events[`storm:${hs.id}`] = { warned: state.day };
      if (fids.length) newStormDev(state, fids, { kind: 'hurricane', hsFt: hs.hsFt, name: `Hurricane ${hs.name}`, days: hs.days ?? 3, warn, historical: hs.id, mudslides: !!hs.mudslides });
    }
    if (hs.day === state.day) stormShutIns(state, hs);
  }
}
// Landfall: infrastructure damage (pipelines, onshore plants, mudslides) shuts the listed fields' wells for shutInDays.
export function stormShutIns(state, hs) {
  const Wm = W(state);
  const rec = (Wm.events[`storm:${hs.id}`] ??= {});
  if (rec.shut) return rec.shut;
  const mult = hasUpgrade(state, 'hurricane_planning') ? 0.7 : 1;
  const until = state.day + (hs.days ?? 3) + Math.round((hs.shutInDays ?? 0) * mult);
  const fids = new Set(hs.fieldIds);
  let n = 0;
  for (const w of allWells(state)) {
    if (!ownWell(w) || !fids.has(w.fieldId) || !w.prod || GONE.has(w.status)) continue;
    if (!w.offshore && !hs.flood) continue;
    w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until);
    n++;
  }
  rec.shut = { day: state.day, untilDay: until, wells: n };
  if (n) notify(state, 'news', { kind: 'disaster', fieldId: hs.fieldIds[0], level: 'warn', text: `Hurricane ${hs.name}: pipelines, processing plants and ports are down — ${n} of your wells stay shut in for about ${until - state.day} days.` });
  return rec.shut;
}

// ---- M4 lease rounds on the M3 machinery ---------------------------------------------------------------------------------
const KIND = { sealed_bid: 'sealed_bonus', fee_auction: 'production_sharing', profit_oil_auction: 'production_sharing' };
const ROUND_NAMES = { chukchi_193: 'Chukchi Sea Lease Sale 193', iraq_round_1: 'Iraq first bid round (service contracts)', iraq_round_2: 'Iraq second bid round', libra_2013: 'Libra pre-salt production-sharing auction',
  mexico_round_1: 'Mexico Round One', buzios_2019: 'Transfer-of-rights surplus auction (Búzios)', bbg_1_2025: 'Gulf of America Lease Sale BBG1', cgom_261: 'Gulf of Mexico Lease Sale 261' };
const M3_SHAPE = new Map();
// m3 leaserounds.leaseRoundById fallback: an M4 round in the M3 record shape (cached).
export function leaseRoundM3Shape(id) {
  if (M3_SHAPE.has(id)) return M3_SHAPE.get(id);
  const r = LEASE_ROUNDS_M4.find((x) => x.id === id);
  if (!r) { M3_SHAPE.set(id, null); return null; }
  const year = yearFrac(r.day);
  const regionId = fieldDef(r.fieldIds[0])?.regionId ?? 'gulf_of_mexico';
  const psc = KIND[r.kind] === 'production_sharing';
  const out = { id: r.id, name: ROUND_NAMES[r.id] ?? (r.areaWide ? `Central Gulf area-wide lease sale ${Math.floor(year)}` : r.id.replace(/_/g, ' ')), year, day: r.day, regionId, fieldIds: [...r.fieldIds],
    kind: KIND[r.kind] ?? 'sealed_bonus', tracts: r.plots, minBidPerPlot: r.minBid ?? (r.signatureBonus ? Math.round(r.signatureBonus / Math.max(1, r.plots) / 100) : 5e6), royalty: r.royalty ?? (psc ? 0.1 : 0.1875),
    rivalsMean: r.areaWide ? 3 : 5, bidShade: 0.4, bidSigma: 0.6, termYears: psc ? 6 : 5, workCommitmentWells: psc ? 1 : 0, baseYear: Math.floor(year), m4: true, note: r.note ?? null };
  M3_SHAPE.set(id, out);
  return out;
}
export function openLeaseRoundM4(state, roundId) {
  const r = leaseRoundM3Shape(roundId);
  if (!r || !r.fieldIds.some((f) => state.fields[f])) return null;
  return openRound(state, roundId);
}

// ---- views --------------------------------------------------------------------------------------------------------------
export function regionWorldView(state, regionId) {
  const y = yf(state);
  const on = m4On(state);
  const sanctions = on ? sanctionsFor(state, regionId).map((s) => ({ id: s.id, from: s.from, until: s.until >= 9999 ? null : s.until, appliesTo: s.appliesTo, effects: JSON.parse(JSON.stringify(s.effects ?? {})), note: s.note ?? '' })) : [];
  const nats = M4_NATIONALIZATIONS.filter((n) => n.regionId === regionId).map((n) => ({ id: n.id, from: n.from, mode: n.mode, share: n.share ?? 0, appliesTo: n.appliesTo, reason: n.reason, state: peekW(state)?.nat?.[n.id] ? { ...peekW(state).nat[n.id] } : null }));
  const cc = on ? capitalControlsAt(regionId, y) : null;
  const price = Math.max(0, spotPrice(state));
  const fid = Object.keys(state.fields ?? {}).find((f) => regionOf(f) === regionId) ?? null;
  return {
    regionId, name: regionDef(regionId)?.name ?? regionId, active: on, sanctions,
    newInvestmentBan: sanctions.some((s) => s.effects.newInvestmentBan), exportBan: sanctions.some((s) => s.effects.exportBan),
    techBan: [...new Set(sanctions.flatMap((s) => s.effects.techBan ?? []))],
    priceMult: on && fid ? Math.round(sanctionPriceMult(state, fid, price) * capitalControlPriceMult(state, fid, price) * 1000) / 1000 : 1,
    divestBy: sanctions.find((s) => s.effects.divestBy)?.effects.divestBy ?? null,
    canDivest: on && sanctions.some((s) => s.effects.divestBy && y < s.effects.divestBy) && hasAssets(state, regionId),
    nationalizations: nats,
    capitalControls: cc ? { ...cc } : null, trappedPesos: Math.round(peekW(state)?.pesos?.[regionId] ?? 0),
    freeze: freezeActive(state, regionId),
    risk: state.regions?.[regionId] ? { ...(regionRiskNow(regionId, y)) } : null,
  };
}
const regionRiskNow = (rid, y) => { try { return regionRisk(rid, y) ?? {}; } catch { return {}; } };
